import type { Route } from "types/not-yet-sorted"

// Which page a path belongs to, per customer. The router guard in App matches
// on the first path segment (dynamic segments are ignored), and so do these.

/** The routes this user can actually open: not disabled; internal ones only for super-admins. */
export const openableRoutes = (routes: Route[], isSuperAdmin: boolean): Route[] =>
  routes.filter((route) => !route.disabled && (!route.internal || isSuperAdmin))

// "/work-items/:id" → "/work-items", "/labelling/*" → "/labelling", "/" → "/"
const routeRoot = (routePath: string) => routePath.split("/:")[0].replace("/*", "") || "/"
const pathRoot = (pathname: string) => `/${pathname.split("/")[1] ?? ""}`

/** The route serving the page at `pathname`, if any. */
export const routeForPath = (pathname: string, routes: Route[]) => routes.find((route) => routeRoot(route.path) === pathRoot(pathname))

/** Whether one of these routes serves the page at `pathname`. */
export const isPathForRoutes = (pathname: string, routes: Route[]): boolean => !!routeForPath(pathname, routes)

/**
 * Where a customer's app starts: "/" for the apps that have it (Network Rail,
 * PB, WAGON, 3RDRAIL), "/dashboard" for roads, else its first page.
 */
export const homePathFor = (routes: Route[]): string => {
  if (routes.some((route) => routeRoot(route.path) === "/")) return "/"
  if (routes.some((route) => routeRoot(route.path) === "/dashboard")) return "/dashboard"
  const first = routes.find((route) => !route.path.includes(":"))
  return first ? routeRoot(first.path) : "/"
}

/**
 * The path to show after switching customer: the same page when the new
 * customer has it — the same component, not just the same path (NR's and
 * roads' /network-view are different pages) — otherwise the new customer's
 * home. A deeper path (/work-items/ABC123, /bookmark-image/ESS/…) names a
 * record of the old customer: the page's own list instead, when the new
 * customer has one at the page's root, else home. Never a path the new
 * customer's app would render blank.
 */
export const pathAfterCustomerSwitch = ({ pathname, from, to }: { pathname: string; from: Route[]; to: Route[] }): string => {
  const before = routeForPath(pathname, from)
  const after = routeForPath(pathname, to)
  if (!before || !after || before.component !== after.component) return homePathFor(to)
  const root = pathRoot(pathname)
  if (root === "/" || pathname.replace(/\/+$/, "") === root) return pathname
  const hasListPage = to.some((route) => route.path === root || route.path === `${root}/*`)
  return hasListPage ? root : homePathFor(to)
}
